import {
  makeStyles,
  mergeClasses,
  Subtitle2,
  tokens,
} from '@fluentui/react-components';
import type { ReactNode } from 'react';

export type Tone = 'brand' | 'success' | 'warning' | 'danger' | 'neutral';

const useStyles = makeStyles({
  // Cartão do Fluent 2: cantos de 8px e sombra, sem contorno. A borda
  // transparente aparece no alto contraste, onde a sombra some.
  card: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorTransparentStroke}`,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow4,
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
    minHeight: '32px',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL} ${tokens.spacingVerticalXS}`,
  },
  tile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '40px',
    height: '40px',
    borderRadius: tokens.borderRadiusLarge,
    fontSize: '20px',
  },
  tileSmall: {
    width: '28px',
    height: '28px',
    borderRadius: tokens.borderRadiusCircular,
    fontSize: '16px',
  },
  brand: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  success: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
  },
  warning: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2,
  },
  danger: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
  },
  neutral: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  stat: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingHorizontalL,
  },
  statText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  statLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
  },
  statValue: {
    fontSize: tokens.fontSizeHero700,
    lineHeight: tokens.lineHeightHero700,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    fontVariantNumeric: 'tabular-nums',
  },
});

/** Superfície arredondada usada por todas as seções de conteúdo. */
export function Card({
  title,
  headerAside,
  className,
  children,
}: {
  title?: string;
  /** Conteúdo alinhado à direita do título. */
  headerAside?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  const styles = useStyles();
  return (
    <section className={mergeClasses(styles.card, className)}>
      {(title !== undefined || headerAside !== undefined) && (
        <div className={styles.cardHeader}>
          {title !== undefined && <Subtitle2 as="h2">{title}</Subtitle2>}
          {headerAside}
        </div>
      )}
      {children}
    </section>
  );
}

/** Ícone sobre fundo tonal: identifica tipos, módulos e indicadores. */
export function IconTile({
  tone = 'brand',
  size = 'medium',
  children,
}: {
  tone?: Tone;
  size?: 'small' | 'medium';
  children: ReactNode;
}) {
  const styles = useStyles();
  return (
    <span
      className={mergeClasses(
        styles.tile,
        size === 'small' && styles.tileSmall,
        styles[tone],
      )}
      aria-hidden
    >
      {children}
    </span>
  );
}

export interface StatItem {
  readonly label: string;
  readonly value: number | string;
  readonly icon: ReactNode;
  readonly tone?: Tone;
}

/** Linha de indicadores no topo da página. */
export function StatRow({ stats }: { stats: readonly StatItem[] }) {
  const styles = useStyles();
  return (
    <div className={styles.stats}>
      {stats.map(({ label, value, icon, tone }) => (
        <div key={label} className={mergeClasses(styles.card, styles.stat)}>
          <IconTile {...(tone === undefined ? {} : { tone })}>{icon}</IconTile>
          <div className={styles.statText}>
            <span className={styles.statLabel}>{label}</span>
            <span className={styles.statValue}>{value}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
